/**
 * Export Inventory Report to Word Document
 * 
 * This service generates a Word document (.docx) containing the complete
 * inventory report with all sections: summary, people, assets, debts, and partition plan.
 */

import {
    Document,
    Packer,
    Paragraph,
    Table,
    TableRow,
    TableCell,
    TextRun,
    HeadingLevel,
    AlignmentType,
    WidthType,
    BorderStyle,
    ShadingType,
    convertInchesToTwip,
    PageBreak,
    ImageRun,
} from 'docx';
import { saveAs } from 'file-saver';
import { drherancaConfig } from '../config';
import { 
    Inventario, 
    PessoaInventario, 
    CalculosInventario, 
    PartilhaResultado,
    AnyAsset,
    Doacao,
} from '../types';
import { SuccessoryProps } from './heirClassifier';
import { formatDate, formatCurrency } from './utils';
import { getAnyAssetPatrimonioValue } from './patrimonioAssetValue';

// ============================================================================
// CONSTANTS
// ============================================================================

// Burgundy/Bordô - Cor padrão Dr. Herança (removendo o #)
const BORDO_COLOR = (drherancaConfig.theme?.accentColor || '#9B2335').replace('#', '');

const COLORS = {
    PRIMARY: BORDO_COLOR,   // Bordô - Dr. Herança
    HEADER_BG: 'F3F4F6',    // Light gray
    BORDER: 'E5E7EB',       // Border gray
    TEXT_DARK: '1F2937',    // Dark text
    TEXT_SECONDARY: '6B7280', // Secondary text
};

// Logo path
const LOGO_PATH = drherancaConfig.assets?.logo || '/static/tenants/drheranca/images/logo/logo.png';

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Creates a styled heading paragraph with bordô color
 */
function createHeading(text: string, level: typeof HeadingLevel[keyof typeof HeadingLevel] = HeadingLevel.HEADING_1): Paragraph {
    const fontSize = level === HeadingLevel.HEADING_1 ? 32 : level === HeadingLevel.HEADING_2 ? 28 : 24;
    return new Paragraph({
        children: [
            new TextRun({
                text: text,
                bold: true,
                size: fontSize,
                color: COLORS.PRIMARY, // Bordô
            }),
        ],
        spacing: { before: 400, after: 200 },
    });
}

/**
 * Creates a subheading paragraph with bordô color
 */
function createSubheading(text: string): Paragraph {
    return new Paragraph({
        children: [
            new TextRun({
                text: text,
                bold: true,
                size: 24, // 12pt
                color: COLORS.PRIMARY, // Bordô
            }),
        ],
        spacing: { before: 300, after: 150 },
    });
}

/**
 * Creates a normal text paragraph
 */
function createText(text: string, bold = false): Paragraph {
    return new Paragraph({
        children: [
            new TextRun({
                text: text,
                size: 22, // 11pt
                bold: bold,
            }),
        ],
        spacing: { after: 100 },
    });
}

/**
 * Creates a label-value paragraph
 */
function createLabelValue(label: string, value: string | undefined | null): Paragraph {
    return new Paragraph({
        children: [
            new TextRun({
                text: `${label}: `,
                bold: true,
                size: 22,
                color: COLORS.TEXT_SECONDARY,
            }),
            new TextRun({
                text: value || 'Não informado',
                size: 22,
            }),
        ],
        spacing: { after: 80 },
    });
}

/**
 * Creates a table header cell
 */
function createHeaderCell(text: string, width?: number): TableCell {
    return new TableCell({
        children: [
            new Paragraph({
                children: [
                    new TextRun({
                        text: text,
                        bold: true,
                        size: 20,
                        color: COLORS.TEXT_DARK,
                    }),
                ],
                alignment: AlignmentType.CENTER,
            }),
        ],
        shading: { fill: COLORS.HEADER_BG, type: ShadingType.CLEAR },
        width: width ? { size: width, type: WidthType.PERCENTAGE } : undefined,
        margins: {
            top: convertInchesToTwip(0.05),
            bottom: convertInchesToTwip(0.05),
            left: convertInchesToTwip(0.1),
            right: convertInchesToTwip(0.1),
        },
    });
}

/**
 * Creates a table data cell
 */
function createDataCell(text: string, alignment: typeof AlignmentType[keyof typeof AlignmentType] = AlignmentType.LEFT, width?: number): TableCell {
    return new TableCell({
        children: [
            new Paragraph({
                children: [
                    new TextRun({
                        text: text,
                        size: 20,
                    }),
                ],
                alignment: alignment,
            }),
        ],
        width: width ? { size: width, type: WidthType.PERCENTAGE } : undefined,
        margins: {
            top: convertInchesToTwip(0.03),
            bottom: convertInchesToTwip(0.03),
            left: convertInchesToTwip(0.1),
            right: convertInchesToTwip(0.1),
        },
    });
}

/**
 * Creates a table with consistent styling
 */
function createStyledTable(rows: TableRow[]): Table {
    return new Table({
        rows: rows,
        width: { size: 100, type: WidthType.PERCENTAGE },
        borders: {
            top: { style: BorderStyle.SINGLE, size: 1, color: COLORS.BORDER },
            bottom: { style: BorderStyle.SINGLE, size: 1, color: COLORS.BORDER },
            left: { style: BorderStyle.SINGLE, size: 1, color: COLORS.BORDER },
            right: { style: BorderStyle.SINGLE, size: 1, color: COLORS.BORDER },
            insideHorizontal: { style: BorderStyle.SINGLE, size: 1, color: COLORS.BORDER },
            insideVertical: { style: BorderStyle.SINGLE, size: 1, color: COLORS.BORDER },
        },
    });
}

function getAssetValue(asset: AnyAsset): number {
    return getAnyAssetPatrimonioValue(asset);
}

/**
 * Determines if an asset is common property (meação) or particular. Respects e_bem_particular from cadastro.
 */
function isDeCujusAssetCommon(asset: AnyAsset, inventario: Inventario): string {
    if ((asset as any).e_bem_particular === true) return 'Particular';

    const regime = inventario.decujus.casamento_regime;

    if (regime === 'Comunhão Universal de Bens' || regime === 'União Estável') {
        return 'Comum';
    }
    if (regime === 'Comunhão Parcial de Bens') {
        return 'Comum';
    }
    return 'Particular';
}

// ============================================================================
// SECTION BUILDERS
// ============================================================================

/**
 * Fetches an image and returns it as an ArrayBuffer
 */
async function fetchImageAsBuffer(imagePath: string): Promise<ArrayBuffer | null> {
    try {
        const response = await fetch(imagePath);
        if (!response.ok) {
            console.warn('Não foi possível carregar o logo:', imagePath);
            return null;
        }
        return await response.arrayBuffer();
    } catch (error) {
        console.warn('Erro ao carregar logo:', error);
        return null;
    }
}

/**
 * Creates the title/cover section with logo
 */
async function createTitleSection(inventario: Inventario): Promise<Paragraph[]> {
    const paragraphs: Paragraph[] = [];
    
    // Try to load and add the logo
    const logoBuffer = await fetchImageAsBuffer(LOGO_PATH);
    if (logoBuffer) {
        paragraphs.push(
            new Paragraph({
                children: [
                    new ImageRun({
                        data: logoBuffer,
                        transformation: {
                            width: 180,
                            height: 50,
                        },
                        type: 'png',
                    }),
                ],
                alignment: AlignmentType.CENTER,
                spacing: { before: 200, after: 300 },
            })
        );
    }
    
    paragraphs.push(
        new Paragraph({
            children: [
                new TextRun({
                    text: 'RELATÓRIO DO INVENTÁRIO',
                    bold: true,
                    size: 48, // 24pt
                    color: COLORS.PRIMARY,
                }),
            ],
            alignment: AlignmentType.CENTER,
            spacing: { before: 400, after: 200 },
        }),
        new Paragraph({
            children: [
                new TextRun({
                    text: inventario.decujus?.nome || 'Nome não informado',
                    size: 36, // 18pt
                    bold: true,
                }),
            ],
            alignment: AlignmentType.CENTER,
            spacing: { after: 100 },
        }),
        new Paragraph({
            children: [
                new TextRun({
                    text: `Gerado em: ${new Date().toLocaleDateString('pt-BR', { 
                        year: 'numeric', 
                        month: 'long', 
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                    })}`,
                    size: 20,
                    color: COLORS.TEXT_SECONDARY,
                }),
            ],
            alignment: AlignmentType.CENTER,
            spacing: { after: 400 },
        })
    );
    
    return paragraphs;
}

/**
 * Creates the summary section with patrimony totals
 */
function createSummarySection(calculos: CalculosInventario): (Paragraph | Table)[] {
    const elements: (Paragraph | Table)[] = [];
    
    elements.push(createHeading('1. RESUMO DO PATRIMÔNIO'));
    
    const summaryData = [
        ['Patrimônio Bruto', formatCurrency(calculos.totalPatrimonioBruto || 0)],
        ['Total de Dívidas', formatCurrency(calculos.totalDividas || 0)],
        ['Patrimônio Líquido', formatCurrency(calculos.totalPatrimonioLiquido || 0)],
        ['Valor da Meação', formatCurrency(calculos.meacaoValor || 0)],
        ['Herança Bruta', formatCurrency(calculos.herancaValorBruto || 0)],
        ['Herança Líquida', formatCurrency(calculos.herancaValorLiquido || 0)],
        ['Legítima (50%)', formatCurrency(calculos.legitimaValor || 0)],
        ['Disponível (50%)', formatCurrency(calculos.disponivelValor || 0)],
        ['ITCMD Total Devido', formatCurrency(calculos.itcmdDevidoTotal || 0)],
        ['Manifesto de Tabelas', calculos.tableSetSummary || 'SP — tabelas vigentes'],
        ['UF da Simulação', calculos.simulationStateCode || 'SP'],
        ['Data da Simulação', calculos.simulationDate || ''],
        ['Unidade Fiscal', calculos.fiscalUnitCode ? `${calculos.fiscalUnitCode} ${formatCurrency(calculos.fiscalUnitValueBrl || 0)}` : ''],
        ['Versão do Termo', calculos.termsVersion || '2026-05-04-v1'],
    ];
    
    const rows = summaryData.map(([label, value]) => 
        new TableRow({
            children: [
                createDataCell(label, AlignmentType.LEFT, 60),
                createDataCell(value, AlignmentType.RIGHT, 40),
            ],
        })
    );
    
    elements.push(createStyledTable([
        new TableRow({
            children: [
                createHeaderCell('Descrição', 60),
                createHeaderCell('Valor', 40),
            ],
        }),
        ...rows,
    ]));

    elements.push(createSubheading('1.1 Termo de responsabilidade'));
    elements.push(createText('Os valores de ITCMD e emolumentos são estimativos e podem variar por atualização de base fiscal, reavaliação de bens, multas/juros, alteração de tabelas, exigências supervenientes e mudança da forma de partilha.'));
    elements.push(createText('Configuração atual do sistema: estimativas para o Estado de São Paulo (SP).'));
    
    return elements;
}

/**
 * Creates the people involved section
 */
function createPeopleSection(
    inventario: Inventario, 
    familyMap: Map<string, PessoaInventario>,
    successoryDataMap: Map<string, SuccessoryProps>
): (Paragraph | Table)[] {
    const elements: (Paragraph | Table)[] = [];
    
    elements.push(createHeading('2. PESSOAS ENVOLVIDAS'));
    
    // De Cujus
    elements.push(createSubheading('2.1 De Cujus (Falecido)'));
    if (inventario.decujus) {
        elements.push(createLabelValue('Nome', inventario.decujus.nome));
        elements.push(createLabelValue('CPF', inventario.decujus.cpf));
        elements.push(createLabelValue('Data de Nascimento', formatDate(inventario.decujus.data_nascimento)));
        elements.push(createLabelValue('Data do Óbito', formatDate(inventario.decujus.data_falecimento || '')));
        elements.push(createLabelValue('Estado Civil', inventario.decujus.estado_civil));
        elements.push(createLabelValue('Regime de Bens', inventario.decujus.casamento_regime));
        
        const filiacao = inventario.decujus.parentIds
            ?.map(id => familyMap.get(id?.toString() || '')?.nome)
            .filter(Boolean)
            .join(' e ');
        if (filiacao) {
            elements.push(createLabelValue('Filiação', filiacao));
        }
    }
    
    // Inventariante
    if (inventario.cliente) {
        elements.push(createSubheading('2.2 Inventariante'));
        elements.push(createLabelValue('Nome', inventario.cliente.nome));
        elements.push(createLabelValue('CPF', inventario.cliente.cpf));
        elements.push(createLabelValue('E-mail', inventario.cliente.email));
        elements.push(createLabelValue('Telefone', inventario.cliente.fone));
        if (inventario.cliente.endereco) {
            const endereco = `${inventario.cliente.endereco.rua || ''}, ${inventario.cliente.endereco.cidade || ''} - ${inventario.cliente.endereco.estado || ''}`;
            elements.push(createLabelValue('Endereço', endereco));
        }
    }
    
    // Cônjuge
    const conjuge = inventario.familiares?.find(p => 
        inventario.decujus?.spouseIds?.includes(p.id?.toString())
    );
    if (conjuge) {
        elements.push(createSubheading('2.3 Cônjuge Supérstite'));
        elements.push(createLabelValue('Nome', conjuge.nome));
        elements.push(createLabelValue('Data de Nascimento', formatDate(conjuge.data_nascimento)));
        elements.push(createLabelValue('Regime de Bens', inventario.decujus.casamento_regime));
        elements.push(createLabelValue('Data do Casamento', formatDate(inventario.decujus.casamento_data || '')));
    }
    
    // Herdeiros
    const herdeiros = inventario.familiares?.filter(p => {
        const props = successoryDataMap.get(p.id?.toString() || '');
        return props?.ehHerdeiroLegitimo || props?.ehHerdeiroNecessario;
    }) || [];
    
    if (herdeiros.length > 0) {
        elements.push(createSubheading('2.4 Herdeiros'));
        
        const headerRow = new TableRow({
            children: [
                createHeaderCell('Nome', 30),
                createHeaderCell('Vínculo', 20),
                createHeaderCell('Data Nasc.', 15),
                createHeaderCell('Herd. Legítimo', 15),
                createHeaderCell('Herd. Necessário', 20),
            ],
        });
        
        const dataRows = herdeiros.map(pessoa => {
            const props = successoryDataMap.get(pessoa.id?.toString() || '');
            return new TableRow({
                children: [
                    createDataCell(pessoa.nome || 'N/A', AlignmentType.LEFT, 30),
                    createDataCell(props?.vinculoDC || 'N/A', AlignmentType.CENTER, 20),
                    createDataCell(formatDate(pessoa.data_nascimento) || 'N/A', AlignmentType.CENTER, 15),
                    createDataCell(props?.ehHerdeiroLegitimo ? 'Sim' : 'Não', AlignmentType.CENTER, 15),
                    createDataCell(props?.ehHerdeiroNecessario ? 'Sim' : 'Não', AlignmentType.CENTER, 20),
                ],
            });
        });
        
        elements.push(createStyledTable([headerRow, ...dataRows]));
    }
    
    return elements;
}

/**
 * Creates asset category table
 */
function createAssetCategoryTable(
    title: string, 
    assets: AnyAsset[], 
    inventario: Inventario
): (Paragraph | Table)[] {
    if (!assets || assets.length === 0) return [];
    
    const elements: (Paragraph | Table)[] = [];
    elements.push(createText(title, true));
    
    const total = assets.reduce((sum, a) => sum + getAssetValue(a), 0);
    
    const headerRow = new TableRow({
        children: [
            createHeaderCell('Descrição', 50),
            createHeaderCell('Origem', 20),
            createHeaderCell('Valor', 30),
        ],
    });
    
    const dataRows = assets.map(asset => 
        new TableRow({
            children: [
                createDataCell((asset as any).descricao || `Item #${(asset as any).id}`, AlignmentType.LEFT, 50),
                createDataCell(isDeCujusAssetCommon(asset, inventario), AlignmentType.CENTER, 20),
                createDataCell(formatCurrency(getAssetValue(asset)), AlignmentType.RIGHT, 30),
            ],
        })
    );
    
    // Total row
    const totalRow = new TableRow({
        children: [
            new TableCell({
                children: [new Paragraph({ children: [new TextRun({ text: 'TOTAL', bold: true, size: 20 })] })],
                columnSpan: 2,
                shading: { fill: COLORS.HEADER_BG, type: ShadingType.CLEAR },
            }),
            new TableCell({
                children: [
                    new Paragraph({
                        children: [new TextRun({ text: formatCurrency(total), bold: true, size: 20 })],
                        alignment: AlignmentType.RIGHT,
                    }),
                ],
                shading: { fill: COLORS.HEADER_BG, type: ShadingType.CLEAR },
            }),
        ],
    });
    
    elements.push(createStyledTable([headerRow, ...dataRows, totalRow]));
    elements.push(new Paragraph({ text: '', spacing: { after: 200 } }));
    
    return elements;
}

/**
 * Creates the assets section
 */
function createAssetsSection(inventario: Inventario): (Paragraph | Table)[] {
    const elements: (Paragraph | Table)[] = [];
    
    elements.push(createHeading('3. BENS DO ESPÓLIO'));
    
    elements.push(...createAssetCategoryTable('Imóveis', inventario.imoveis || [], inventario));
    elements.push(...createAssetCategoryTable('Veículos', inventario.veiculos || [], inventario));
    elements.push(...createAssetCategoryTable('Investimentos e Contas', inventario.investimentos || [], inventario));
    elements.push(...createAssetCategoryTable('Participações Societárias', inventario.participacoes_societarias || [], inventario));
    elements.push(...createAssetCategoryTable('Créditos a Receber', inventario.creditos || [], inventario));
    elements.push(...createAssetCategoryTable('Criptoativos', inventario.criptomoedas || [], inventario));
    elements.push(...createAssetCategoryTable('Outros Bens e Direitos', inventario.outros_bens || [], inventario));
    elements.push(...createAssetCategoryTable('Bens no Exterior', inventario.bens_internacionais || [], inventario));
    elements.push(...createAssetCategoryTable('Seguros', inventario.seguros || [], inventario));
    
    return elements;
}

/**
 * Creates the debts and donations section
 */
/** Extrai itens de financiamento ativo (saldo devedor) de bens do inventário */
function getFinanciamentosFromInventory(inventario: Inventario): Array<{ id: string; descricao: string; saldo_devedor: number; tipo: string }> {
    const items: Array<{ id: string; descricao: string; saldo_devedor: number; tipo: string }> = [];
    const add = (arr: any[] | undefined, tipo: string) => {
        (arr || []).forEach((a: any) => {
            if (a?.esta_financiado !== true) return;
            const saldo = Number(a?.saldo_devedor) || 0;
            if (saldo > 0) {
                items.push({
                    id: `${tipo}-${a.id}`,
                    descricao: a.descricao || `${tipo} #${a.id}`,
                    saldo_devedor: saldo,
                    tipo,
                });
            }
        });
    };
    add(inventario.imoveis, 'Imóvel');
    add(inventario.veiculos, 'Veículo');
    add(inventario.outros_bens, 'Outro bem');
    add(inventario.bens_internacionais, 'Bem internacional');
    return items;
}

function createDebtsAndDonationsSection(
    inventario: Inventario,
    familyMap: Map<string, PessoaInventario>,
    assetMap: Map<string | number, AnyAsset>
): (Paragraph | Table)[] {
    const elements: (Paragraph | Table)[] = [];
    
    elements.push(createHeading('4. DÍVIDAS E DOAÇÕES'));
    
    const financiamentos = getFinanciamentosFromInventory(inventario);
    const dividas = inventario.dividas || [];
    
    // 4.1 Dívidas cadastradas
    if (dividas.length > 0) {
        elements.push(createSubheading('4.1 Dívidas Cadastradas'));
        
        const totalDividas = dividas.reduce((sum, d) => sum + (d.saldo_devedor || 0), 0);
        
        const headerRow = new TableRow({
            children: [
                createHeaderCell('Descrição', 70),
                createHeaderCell('Valor', 30),
            ],
        });
        
        const dataRows = dividas.map(divida => 
            new TableRow({
                children: [
                    createDataCell(divida.descricao || `Dívida #${divida.id}`, AlignmentType.LEFT, 70),
                    createDataCell(formatCurrency(divida.saldo_devedor || 0), AlignmentType.RIGHT, 30),
                ],
            })
        );
        
        const totalRow = new TableRow({
            children: [
                new TableCell({
                    children: [new Paragraph({ children: [new TextRun({ text: 'Subtotal', bold: true, size: 20 })] })],
                    shading: { fill: COLORS.HEADER_BG, type: ShadingType.CLEAR },
                }),
                new TableCell({
                    children: [
                        new Paragraph({
                            children: [new TextRun({ text: formatCurrency(totalDividas), bold: true, size: 20 })],
                            alignment: AlignmentType.RIGHT,
                        }),
                    ],
                    shading: { fill: COLORS.HEADER_BG, type: ShadingType.CLEAR },
                }),
            ],
        });
        
        elements.push(createStyledTable([headerRow, ...dataRows, totalRow]));
    }
    
    // 4.2 Saldos de Financiamentos (bens em financiamento)
    if (financiamentos.length > 0) {
        elements.push(createSubheading('4.2 Saldos de Financiamentos'));
        
        const totalFinanciamentos = financiamentos.reduce((sum, i) => sum + i.saldo_devedor, 0);
        
        const headerRow = new TableRow({
            children: [
                createHeaderCell('Bem (tipo)', 70),
                createHeaderCell('Saldo devedor', 30),
            ],
        });
        
        const dataRows = financiamentos.map(item => 
            new TableRow({
                children: [
                    createDataCell(`${item.descricao} (${item.tipo})`, AlignmentType.LEFT, 70),
                    createDataCell(formatCurrency(item.saldo_devedor), AlignmentType.RIGHT, 30),
                ],
            })
        );
        
        const totalRow = new TableRow({
            children: [
                new TableCell({
                    children: [new Paragraph({ children: [new TextRun({ text: 'Subtotal', bold: true, size: 20 })] })],
                    shading: { fill: COLORS.HEADER_BG, type: ShadingType.CLEAR },
                }),
                new TableCell({
                    children: [
                        new Paragraph({
                            children: [new TextRun({ text: formatCurrency(totalFinanciamentos), bold: true, size: 20 })],
                            alignment: AlignmentType.RIGHT,
                        }),
                    ],
                    shading: { fill: COLORS.HEADER_BG, type: ShadingType.CLEAR },
                }),
            ],
        });
        
        elements.push(createStyledTable([headerRow, ...dataRows, totalRow]));
    }
    
    // Doações
    const doacoes = inventario.doacoes || [];
    if (doacoes.length > 0) {
        const sec = 2 + (dividas.length > 0 ? 1 : 0) + (financiamentos.length > 0 ? 1 : 0);
        elements.push(createSubheading(`4.${sec} Doações e Colação`));
        
        const totalDoacoes = doacoes.reduce((sum, d) => sum + (d.valor || 0), 0);
        
        const headerRow = new TableRow({
            children: [
                createHeaderCell('Descrição', 40),
                createHeaderCell('Donatário(s)', 35),
                createHeaderCell('Valor', 25),
            ],
        });
        
        const dataRows = doacoes.map((doacao: Doacao) => {
            const relatedAsset = assetMap.get(doacao.bem_relacionado);
            const assetDescription = (relatedAsset as any)?.descricao || doacao.observacao || 'Doação';
            const donatarios = doacao.doacao_pessoas_inventario
                ?.map(d => familyMap.get(d?.pessoa_inventario_id?.toString() || '')?.nome)
                .filter(Boolean)
                .join(', ') || 'Não informado';
            
            return new TableRow({
                children: [
                    createDataCell(assetDescription, AlignmentType.LEFT, 40),
                    createDataCell(donatarios, AlignmentType.LEFT, 35),
                    createDataCell(formatCurrency(doacao.valor || 0), AlignmentType.RIGHT, 25),
                ],
            });
        });
        
        const totalRow = new TableRow({
            children: [
                new TableCell({
                    children: [new Paragraph({ children: [new TextRun({ text: 'TOTAL', bold: true, size: 20 })] })],
                    columnSpan: 2,
                    shading: { fill: COLORS.HEADER_BG, type: ShadingType.CLEAR },
                }),
                new TableCell({
                    children: [
                        new Paragraph({
                            children: [new TextRun({ text: formatCurrency(totalDoacoes), bold: true, size: 20 })],
                            alignment: AlignmentType.RIGHT,
                        }),
                    ],
                    shading: { fill: COLORS.HEADER_BG, type: ShadingType.CLEAR },
                }),
            ],
        });
        
        elements.push(createStyledTable([headerRow, ...dataRows, totalRow]));
    }
    
    if (dividas.length === 0 && financiamentos.length === 0 && doacoes.length === 0) {
        elements.push(createText('Nenhuma dívida, obrigação ou doação declarada.'));
    }
    
    return elements;
}

/**
 * Creates the partition plan section
 */
function createPartitionSection(
    partilhaData: PartilhaResultado[],
    successoryDataMap: Map<string, SuccessoryProps>
): (Paragraph | Table)[] {
    const elements: (Paragraph | Table)[] = [];
    
    elements.push(createHeading('5. PLANO DE PARTILHA'));
    
    // Por Herdeiro
    elements.push(createSubheading('5.1 Partilha por Herdeiro'));
    
    if (partilhaData && partilhaData.length > 0) {
        const headerRow = new TableRow({
            children: [
                createHeaderCell('Herdeiro', 25),
                createHeaderCell('Meação', 15),
                createHeaderCell('Herança', 15),
                createHeaderCell('ITCMD', 15),
                createHeaderCell('Valor Líquido', 15),
                createHeaderCell('Vínculo', 15),
            ],
        });
        
        const dataRows = partilhaData.map(partilha => {
            const props = successoryDataMap.get(partilha.herdeiroId?.toString() || '');
            return new TableRow({
                children: [
                    createDataCell(partilha.herdeiroNome || 'N/A', AlignmentType.LEFT, 25),
                    createDataCell(formatCurrency(partilha.valorMeacao || 0), AlignmentType.RIGHT, 15),
                    createDataCell(formatCurrency(partilha.valorHerancaLiquida || 0), AlignmentType.RIGHT, 15),
                    createDataCell(formatCurrency(partilha.itcmdDevido || 0), AlignmentType.RIGHT, 15),
                    createDataCell(formatCurrency(partilha.valorRecebidoLiquido || 0), AlignmentType.RIGHT, 15),
                    createDataCell(props?.vinculoDC || 'N/A', AlignmentType.CENTER, 15),
                ],
            });
        });
        
        // Totals row
        const totalMeacao = partilhaData.reduce((sum, p) => sum + (p.valorMeacao || 0), 0);
        const totalHeranca = partilhaData.reduce((sum, p) => sum + (p.valorHerancaLiquida || 0), 0);
        const totalItcmd = partilhaData.reduce((sum, p) => sum + (p.itcmdDevido || 0), 0);
        const totalLiquido = partilhaData.reduce((sum, p) => sum + (p.valorRecebidoLiquido || 0), 0);
        
        const totalRow = new TableRow({
            children: [
                new TableCell({
                    children: [new Paragraph({ children: [new TextRun({ text: 'TOTAL', bold: true, size: 20 })] })],
                    shading: { fill: COLORS.HEADER_BG, type: ShadingType.CLEAR },
                }),
                new TableCell({
                    children: [new Paragraph({ children: [new TextRun({ text: formatCurrency(totalMeacao), bold: true, size: 20 })], alignment: AlignmentType.RIGHT })],
                    shading: { fill: COLORS.HEADER_BG, type: ShadingType.CLEAR },
                }),
                new TableCell({
                    children: [new Paragraph({ children: [new TextRun({ text: formatCurrency(totalHeranca), bold: true, size: 20 })], alignment: AlignmentType.RIGHT })],
                    shading: { fill: COLORS.HEADER_BG, type: ShadingType.CLEAR },
                }),
                new TableCell({
                    children: [new Paragraph({ children: [new TextRun({ text: formatCurrency(totalItcmd), bold: true, size: 20 })], alignment: AlignmentType.RIGHT })],
                    shading: { fill: COLORS.HEADER_BG, type: ShadingType.CLEAR },
                }),
                new TableCell({
                    children: [new Paragraph({ children: [new TextRun({ text: formatCurrency(totalLiquido), bold: true, size: 20 })], alignment: AlignmentType.RIGHT })],
                    shading: { fill: COLORS.HEADER_BG, type: ShadingType.CLEAR },
                }),
                new TableCell({
                    children: [new Paragraph({ text: '' })],
                    shading: { fill: COLORS.HEADER_BG, type: ShadingType.CLEAR },
                }),
            ],
        });
        
        elements.push(createStyledTable([headerRow, ...dataRows, totalRow]));
        
        // Detalhamento por herdeiro (bens recebidos)
        elements.push(new Paragraph({ text: '', spacing: { after: 200 } }));
        elements.push(createSubheading('5.2 Detalhamento por Herdeiro'));
        
        partilhaData.forEach(partilha => {
            if (partilha.assetShares && partilha.assetShares.length > 0) {
                elements.push(createText(`${partilha.herdeiroNome}:`, true));
                
                const assetHeaderRow = new TableRow({
                    children: [
                        createHeaderCell('Bem', 40),
                        createHeaderCell('Categoria', 20),
                        createHeaderCell('Tipo', 20),
                        createHeaderCell('Valor', 20),
                    ],
                });
                
                const assetRows = partilha.assetShares.map(share => 
                    new TableRow({
                        children: [
                            createDataCell(share.assetDescription || 'N/A', AlignmentType.LEFT, 40),
                            createDataCell(share.categoryLabel || share.category || 'N/A', AlignmentType.CENTER, 20),
                            createDataCell(share.type || 'N/A', AlignmentType.CENTER, 20),
                            createDataCell(formatCurrency(share.value || 0), AlignmentType.RIGHT, 20),
                        ],
                    })
                );
                
                elements.push(createStyledTable([assetHeaderRow, ...assetRows]));
                elements.push(new Paragraph({ text: '', spacing: { after: 150 } }));
            }
        });
    } else {
        elements.push(createText('Nenhum dado de partilha disponível.'));
    }
    
    return elements;
}

// ============================================================================
// MAIN EXPORT FUNCTION
// ============================================================================

export interface ExportToWordParams {
    inventario: Inventario;
    calculos: CalculosInventario;
    familyMap: Map<string, PessoaInventario>;
    assetMap: Map<string | number, AnyAsset>;
    successoryDataMap: Map<string, SuccessoryProps>;
    partilhaData: PartilhaResultado[];
}

/**
 * Exports the inventory report to a Word document
 */
export async function exportInventoryToWord(params: ExportToWordParams): Promise<void> {
    const { inventario, calculos, familyMap, assetMap, successoryDataMap, partilhaData } = params;
    
    // Build document sections
    const titleSection = await createTitleSection(inventario);
    const summarySection = createSummarySection(calculos);
    const peopleSection = createPeopleSection(inventario, familyMap, successoryDataMap);
    const assetsSection = createAssetsSection(inventario);
    const debtsSection = createDebtsAndDonationsSection(inventario, familyMap, assetMap);
    const partitionSection = createPartitionSection(partilhaData, successoryDataMap);
    
    // Create the document
    const doc = new Document({
        creator: 'Dr. Herança',
        title: `Relatório do Inventário - ${inventario.decujus?.nome || 'Inventário'}`,
        description: 'Relatório completo do inventário',
        styles: {
            default: {
                document: {
                    run: {
                        font: 'Calibri',
                        size: 22, // 11pt
                    },
                },
            },
            paragraphStyles: [
                {
                    id: 'Normal',
                    name: 'Normal',
                    run: {
                        font: 'Calibri',
                        size: 22,
                    },
                },
            ],
        },
        sections: [
            {
                properties: {
                    page: {
                        margin: {
                            top: convertInchesToTwip(1),
                            right: convertInchesToTwip(1),
                            bottom: convertInchesToTwip(1),
                            left: convertInchesToTwip(1),
                        },
                    },
                },
                children: [
                    ...titleSection,
                    ...summarySection,
                    new Paragraph({ children: [new PageBreak()] }),
                    ...peopleSection,
                    new Paragraph({ children: [new PageBreak()] }),
                    ...assetsSection,
                    ...debtsSection,
                    new Paragraph({ children: [new PageBreak()] }),
                    ...partitionSection,
                ],
            },
        ],
    });
    
    // Generate and save the file
    const blob = await Packer.toBlob(doc);
    const fileName = `Relatorio_Inventario_${inventario.decujus?.nome?.replace(/\s+/g, '_') || 'Export'}_${new Date().toISOString().slice(0, 10)}.docx`;
    saveAs(blob, fileName);
}

